import {
  EntryPatchRequest,
  EventType,
  HoursString,
  NonNegativeDecimalString,
  WeatherCondition,
  type EntryFull,
} from '@siteday/shared';
import { z } from 'zod';

import { newId } from '@siteday/shared';

import type { Snapshot } from '@/lib/diff';

/**
 * W4 edit form. Inputs are strings; `toDocument` turns the values into the
 * shared DayEntryDocument shape that PATCH /entries/:id expects. The reason
 * rule (min 5 chars) is the shared EntryPatchRequest rule, not a local copy.
 */
export const EntryEditSchema = z.object({
  reason: EntryPatchRequest.shape.reason,
  notes: z.string().max(4000),
  weatherCondition: z.union([WeatherCondition, z.literal('')]),
  temperatureF: z.string().regex(/^-?\d{0,3}$/, 'whole degrees'),
  crewLines: z.array(
    z.object({ id: z.string(), userId: z.string(), userName: z.string(), hours: HoursString }),
  ),
  equipmentLines: z.array(
    z.object({
      id: z.string(),
      equipmentId: z.string(),
      equipmentName: z.string(),
      hours: HoursString,
    }),
  ),
  quantityLines: z.array(
    z.object({
      id: z.string(),
      jobCostCodeId: z.string(),
      code: z.string(),
      description: z.string(),
      unit: z.string(),
      quantity: NonNegativeDecimalString,
    }),
  ),
  events: z.array(
    z.object({
      id: z.string(),
      type: EventType,
      durationMinutes: z.string().regex(/^\d{0,4}$/, 'minutes'),
      description: z.string().max(1000),
    }),
  ),
});
export type EntryEditInput = z.input<typeof EntryEditSchema>;
export type EntryEditValues = z.output<typeof EntryEditSchema>;

export type EntryDocument = z.input<typeof EntryPatchRequest>['document'];

/** Form values from the current entry, or from a revision snapshot ("Edit merged" uses the field version). */
export function valuesFromEntry(entry: EntryFull, seed?: Snapshot | null): EntryEditInput {
  const live = <T extends { deletedAt?: string | null | undefined }>(xs: T[] | undefined) =>
    (xs ?? []).filter((x) => !x.deletedAt);
  const crew = seed
    ? live(seed.crewLines).map((l) => ({
        id: l.id,
        userId: l.userId,
        userName:
          l.userName ??
          entry.crewLines.find((c) => c.userId === l.userId)?.userName ??
          'Crew member',
        hours: l.hours,
      }))
    : entry.crewLines.map((l) => ({
        id: l.id,
        userId: l.userId,
        userName: l.userName,
        hours: l.hours,
      }));
  const equipment = seed
    ? live(seed.equipmentLines).map((l) => ({
        id: l.id,
        equipmentId: l.equipmentId,
        equipmentName:
          l.equipmentName ??
          entry.equipmentLines.find((e) => e.equipmentId === l.equipmentId)?.equipmentName ??
          'Machine',
        hours: l.hours,
      }))
    : entry.equipmentLines.map((l) => ({
        id: l.id,
        equipmentId: l.equipmentId,
        equipmentName: l.equipmentName,
        hours: l.hours,
      }));
  const seedQty = seed ? live(seed.quantityLines) : null;
  const quantities = entry.quantityLines.map((q) => {
    const s = seedQty?.find((x) => x.jobCostCodeId === q.jobCostCodeId);
    return {
      id: s?.id ?? q.id,
      jobCostCodeId: q.jobCostCodeId,
      code: q.code,
      description: q.description,
      unit: q.unit,
      quantity: s ? s.quantity : q.quantity,
    };
  });
  const events = seed
    ? live(seed.events).map((e) => ({
        id: e.id,
        type: EventType.safeParse(e.type).success
          ? (e.type as z.infer<typeof EventType>)
          : ('other' as const),
        durationMinutes: e.durationMinutes === null ? '' : String(e.durationMinutes),
        description: e.description ?? '',
      }))
    : entry.events.map((e) => ({
        id: e.id,
        type: e.type,
        durationMinutes: e.durationMinutes === null ? '' : String(e.durationMinutes),
        description: e.description ?? '',
      }));
  return {
    reason: '',
    notes: (seed ? (seed.notes ?? null) : entry.notes) ?? '',
    weatherCondition: (seed
      ? WeatherCondition.safeParse(seed.weatherCondition).success
        ? (seed.weatherCondition as z.infer<typeof WeatherCondition>)
        : ''
      : (entry.weatherCondition ?? '')) as z.infer<typeof WeatherCondition> | '',
    temperatureF:
      (seed ? (seed.temperatureF ?? null) : entry.temperatureF) === null
        ? ''
        : String(seed ? seed.temperatureF : entry.temperatureF),
    crewLines: crew,
    equipmentLines: equipment,
    quantityLines: quantities,
    events,
  };
}

/** The wire document: full lines (zero quantities included so the server can soft-delete), no version fields. */
export function toDocument(entry: EntryFull, v: EntryEditValues): EntryDocument {
  return {
    id: entry.id,
    jobId: entry.jobId,
    foremanId: entry.foremanId,
    entryDate: entry.entryDate,
    status: entry.status,
    weatherCondition: v.weatherCondition === '' ? null : v.weatherCondition,
    temperatureF: v.temperatureF === '' || v.temperatureF === '-' ? null : Number(v.temperatureF),
    notes: v.notes.trim() === '' ? null : v.notes,
    finishedAt: entry.finishedAt,
    crewLines: v.crewLines.map((l) => ({ id: l.id, userId: l.userId, hours: l.hours })),
    equipmentLines: v.equipmentLines.map((l) => ({
      id: l.id,
      equipmentId: l.equipmentId,
      hours: l.hours,
    })),
    quantityLines: v.quantityLines.map((l) => ({
      id: l.id,
      jobCostCodeId: l.jobCostCodeId,
      quantity: l.quantity,
    })),
    events: v.events.map((e) => ({
      id: e.id,
      type: e.type,
      durationMinutes: e.durationMinutes === '' ? null : Number(e.durationMinutes),
      description: e.description.trim() === '' ? null : e.description,
    })),
    photos: entry.photos.map((p) => ({
      id: p.id,
      takenAt: p.takenAt,
      caption: p.caption,
      width: p.width,
      height: p.height,
      bytes: p.bytes,
    })),
  };
}

export const newLineId = () => newId();
